import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  ThemeEntryDto,
  TimeZoneEntryDto,
  UiSettingsControllerService,
  UserControllerService,
  UserStateDataEntryValue,
  VersionControllerService,
} from '@osos/shared/api';
import {
  GeneralModelPresenterModel,
  LayoutModeType,
  ManagedHttpContext,
  MenuMode,
  SidebarVisibility,
  ThemePresenterModel,
  TimeZonePresenterModel,
  UserStateDataPresenterModel,
} from '@osos/shared/types';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ThemeFactoryService } from '../../factories/theme-factory/theme-factory.service';
import { TimeZoneFactoryService } from '../../factories/time-zone-factory/time-zone-factory.service';
import { UserStateDataEntryFactoryService } from '../../factories/user-state-data-entry-factory/user-state-data-entry-factory.service';
import { UserStateExcludingFactoryService } from '../../factories/user-state-excluding-factory/user-state-excluding-factory.service';
import { AppState, AppStateModel } from '../app/app.state';
import { AuthState } from '../auth/auth.state';
import { SetLanguage } from '../translation/language.actions';
import { UserPreferencesActions } from './user-preferences.actions';

/**
 * TODO: The multiple user pattern should be replaced with the single user pattern...
 * once we retrieve the user preferences from the backend.
 */

type TourMode = {
  supported: boolean;
  enabled: boolean;
  currentTour: number;
  toursStarted?: boolean;
};

export interface UserPreferences {
  fontSize?: number;
  layoutMode?: LayoutModeType;
  menuMode?: MenuMode;
  firstLogin?: boolean;
  isRTL?: boolean;
  tourMode?: TourMode;
  sidebarVisibility?: SidebarVisibility;
}

export interface DelegationPreferences {
  activeDelegationId: string | null;
}

export interface UserPreferencesStateModel {
  user: { [userId: string]: UserPreferences };
  themes: ThemePresenterModel[];
  timeZones: TimeZonePresenterModel[];
  userState: UserStateDataPresenterModel;
  dates: GeneralModelPresenterModel[];
  times: GeneralModelPresenterModel[];
  dateTimes: GeneralModelPresenterModel[];
  userUiSettings?: any;
  backendVersion?: string;
  delegation: DelegationPreferences;
}

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  fontSize: 14,
  layoutMode: 'light',
  menuMode: MenuMode.Static,
  firstLogin: true,
  isRTL: false,
  tourMode: {
    supported: true,
    enabled: true,
    currentTour: null,
    toursStarted: false,
  },
  sidebarVisibility: SidebarVisibility.None,
};

@State<UserPreferencesStateModel>({
  name: 'preferences',
  defaults: {
    user: {},
    themes: [],
    timeZones: [],
    userState: {},
    dates: [],
    times: [],
    dateTimes: [],
    delegation: {
      activeDelegationId: null,
    },
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class UserPreferencesState {
  private get currentUserId(): string {
    return this.store.selectSnapshot(AuthState.userId);
  }

  constructor(
    private store: Store,
    private settingService: UiSettingsControllerService,
    private themeFactoryService: ThemeFactoryService,
    private timeZoneFactoryService: TimeZoneFactoryService,
    private UserStateDataEntryFactoryService: UserStateDataEntryFactoryService,
    private UserStateExcludingFactoryService: UserStateExcludingFactoryService,
    private userControllerService: UserControllerService,
    private versionControllerService: VersionControllerService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  @Selector([UserPreferencesState, AuthState.userId])
  static userPreferences(
    state: UserPreferencesStateModel,
    userId: string,
  ): UserPreferences {
    // TODO: The default should be removed once we retrieve the preferences from the backend.
    return state.user[userId] ?? DEFAULT_USER_PREFERENCES;
  }

  @Selector([
    UserPreferencesState,
    AuthState.userId,
    AppState.headlessModeEnabled,
  ])
  static userFirstLogin(
    state: UserPreferencesStateModel,
    userId: string,
    headlessModeEnabled: AppStateModel['headlessMode']['enabled'],
  ): boolean {
    if (headlessModeEnabled) return false;
    return state.user[userId]?.firstLogin ?? true;
  }

  @Selector([UserPreferencesState, AuthState.userId])
  static layoutMode(state: UserPreferencesStateModel, userId: string): string {
    return state.user[userId]?.layoutMode;
  }

  @Selector([UserPreferencesState, AuthState.userId])
  static tourMode(state: UserPreferencesStateModel, userId: string): TourMode {
    return (
      state.user[userId]?.tourMode ?? {
        supported: true,
        enabled: true,
        currentTour: null,
        toursStarted: false,
      }
    );
  }

  @Selector([UserPreferencesState.tourMode])
  static tourEnabled(tourMode: TourMode): boolean {
    return tourMode?.enabled ?? true;
  }

  @Selector([UserPreferencesState.tourMode])
  static tourSupported(tourMode: TourMode): boolean {
    return tourMode?.supported ?? true;
  }

  @Selector([UserPreferencesState, AuthState.userId])
  static toursCanStarted(
    state: UserPreferencesStateModel,
    userId: string,
  ): boolean {
    return state.user[userId]?.tourMode?.toursStarted ?? false;
  }

  @Selector([UserPreferencesState, AuthState.userId])
  static sidebarVisibility(
    state: UserPreferencesStateModel,
    userId: string,
  ): SidebarVisibility {
    return state.user[userId]?.sidebarVisibility;
  }
  @Selector([UserPreferencesState])
  static selectTheme(state: UserPreferencesStateModel): ThemePresenterModel[] {
    return state.themes;
  }

  @Selector([UserPreferencesState])
  static backendVersion(state: UserPreferencesStateModel): string {
    return state.backendVersion;
  }
  @Selector([UserPreferencesState])
  static selectTimeZone(
    state: UserPreferencesStateModel,
  ): TimeZonePresenterModel[] {
    return state.timeZones;
  }
  @Selector([UserPreferencesState])
  static selectDate(
    state: UserPreferencesStateModel,
  ): GeneralModelPresenterModel[] {
    return state.dates;
  }
  @Selector([UserPreferencesState])
  static selectTime(
    state: UserPreferencesStateModel,
  ): GeneralModelPresenterModel[] {
    return state.times;
  }
  @Selector([UserPreferencesState])
  static selectDateTime(
    state: UserPreferencesStateModel,
  ): GeneralModelPresenterModel[] {
    return state.dateTimes;
  }
  @Selector([UserPreferencesState])
  static selectUserState(
    state: UserPreferencesStateModel,
  ): UserStateDataPresenterModel {
    return state.userState;
  }
  @Selector([UserPreferencesState])
  static selectUseUiSetting(state: UserPreferencesStateModel): any {
    return state.userUiSettings;
  }

  @Selector([UserPreferencesState])
  static activeDelegationId(state: UserPreferencesStateModel): string | null {
    return state.delegation?.activeDelegationId;
  }

  @Action(UserPreferencesActions.InitUserPreferences)
  initUserPreferences({
    setState,
    getState,
  }: StateContext<UserPreferencesStateModel>): void {
    const { user } = getState();

    // Set the default user preferences if it's not already there:
    if (!user[this.currentUserId]) {
      setState(
        patch({
          user: patch({
            [this.currentUserId]: {
              ...DEFAULT_USER_PREFERENCES,
              // For anonymous user, the tour should be disabled
              ...(!this.currentUserId && {
                tourMode: { enabled: false } as TourMode,
              }),
            },
          }),
        }),
      );
    }
  }

  @Action(UserPreferencesActions.SetUserPreferences)
  setUserPreferences(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.SetUserPreferences,
  ): void {
    const { user: userUi } = getState();
    setState(
      patch({
        user: patch({
          [this.currentUserId]: {
            ...(userUi[this.currentUserId] ?? {}),
            ...payload,
          },
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.SetFontSize)
  setFontSize(
    { setState }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.SetFontSize,
  ): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({ fontSize: payload }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.SetLayoutMode)
  setLayoutMode(
    { setState }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.SetLayoutMode,
  ): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({
            layoutMode: payload,
          }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.SetMenuMode)
  setMenuMode(
    { setState }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.SetMenuMode,
  ): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({ menuMode: payload }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.SetIsFirstLogin)
  setIsFirstLogin(
    { setState }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.SetIsFirstLogin,
  ): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({ firstLogin: payload }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.SetTourMode)
  setTourMode(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {
      payload: { supported, enabled, currentTour, toursStarted },
    }: UserPreferencesActions.SetTourMode,
  ): void {
    const { tourMode } = getState().user[this.currentUserId];

    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({
            tourMode: patch({
              supported: supported ?? tourMode.supported,
              enabled: enabled ?? tourMode.enabled,
              currentTour: currentTour ?? tourMode.currentTour,
              toursStarted: toursStarted ?? tourMode.toursStarted,
            }),
          }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.StartTours)
  startTours({ setState }: StateContext<UserPreferencesStateModel>): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({
            tourMode: patch({
              enabled: true,
              currentTour: null,
              toursStarted: true,
            }),
          }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.StopTours)
  stopTours({ setState }: StateContext<UserPreferencesStateModel>): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({
            tourMode: patch({ toursStarted: false }),
          }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.DisableTours)
  disableTours({ setState }: StateContext<UserPreferencesStateModel>): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({
            tourMode: patch({
              supported: false,
              enabled: false,
              currentTour: null,
              toursStarted: false,
            }),
          }),
        }),
      }),
    );
  }

  @Action(UserPreferencesActions.UpdateSidebarVisibility)
  updateSidebarVisibility(
    { setState }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.UpdateSidebarVisibility,
  ): void {
    setState(
      patch({
        user: patch({
          [this.currentUserId]: patch<UserPreferences>({
            sidebarVisibility: payload,
          }),
        }),
      }),
    );
  }
  @Action(UserPreferencesActions.DisplayAvailableThemes)
  displayAvailableThemes(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.DisplayAvailableThemes,
  ): Observable<any> {
    // const existingThemes = getState().themes;
    // if (existingThemes?.length) {
    //   return of(existingThemes);
    // }
    return this.settingService
      .getAvailableThemes({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        map((themes: ThemeEntryDto[]) =>
          themes.map((theme) =>
            this.themeFactoryService.createPresenterModel(theme),
          ),
        ),
        tap((themes) => {
          setState(
            patch<UserPreferencesStateModel>({
              themes: themes,
            }),
          );
        }),
      );
  }
  @Action(UserPreferencesActions.DisplayAvailableTimeZones)
  displayAvailableTimeZone(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.DisplayAvailableTimeZones,
  ): Observable<any> {
    // const existingTimezones = getState().timeZones;
    // if (existingTimezones?.length) {
    //   return of(existingTimezones);
    // }
    return this.settingService
      .getAvailableTimeZones({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        map((timeZones: TimeZoneEntryDto[]) =>
          timeZones.map((timeZone) =>
            this.timeZoneFactoryService.createPresenterModel(timeZone),
          ),
        ),
        tap((timeZones) => {
          setState(
            patch<UserPreferencesStateModel>({
              timeZones: timeZones,
            }),
          );
        }),
      );
  }
  @Action(UserPreferencesActions.DisplayAvailableDates)
  displayAvailableDates(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.DisplayAvailableDates,
  ): Observable<any> {
    // const existingDates = getState().dates;
    // if (existingDates?.length) {
    //   return of(existingDates);
    // }
    return this.settingService
      .getAvailableDateFormats({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((dates) => {
          setState(
            patch<UserPreferencesStateModel>({
              dates: dates,
            }),
          );
        }),
      );
  }
  @Action(UserPreferencesActions.DisplayAvailableTimes)
  displayAvailableTimes(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.DisplayAvailableTimes,
  ): Observable<any> {
    // const existingTimes = getState().times;
    // if (existingTimes?.length) {
    //   return of(existingTimes);
    // }
    return this.settingService
      .getAvailableTimeFormats({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((times) => {
          setState(
            patch<UserPreferencesStateModel>({
              times: times,
            }),
          );
        }),
      );
  }
  @Action(UserPreferencesActions.DisplayAvailableDateTimes)
  displayAvailableDateTimes(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.DisplayAvailableDateTimes,
  ): Observable<any> {
    // const existingDateTimes = getState().dateTimes;
    // if (existingDateTimes?.length) {
    //   return of(existingDateTimes);
    // }
    return this.settingService
      .getAvailableDateTimeFormats({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((dateTimes) => {
          setState(
            patch<UserPreferencesStateModel>({
              dateTimes: dateTimes,
            }),
          );
        }),
      );
  }
  @Action(UserPreferencesActions.FetchUserStateData)
  fetchUserStateData(
    { setState, dispatch, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.FetchUserStateData,
  ): Observable<any> {
    return forkJoin({
      userState: this.userControllerService.getUserState({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      }),
      uiSetting: this.settingService.getUserUiSettings({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      }),
    }).pipe(
      map((res) => ({
        userState: this.UserStateDataEntryFactoryService.createPresenterModel(
          res.userState,
        ),
        uiSetting: res.uiSetting,
      })),
      tap((result) => {
        const config = sessionStorage.getItem('configuration');
        const { user } = getState();
        const tourConfig =
          user[this.currentUserId]?.tourMode ??
          DEFAULT_USER_PREFERENCES.tourMode;
        const userStateSetting: UserStateDataPresenterModel = config
          ? JSON.parse(config)
          : {
              dateFormat:
                result.userState?.dateFormat ?? result.uiSetting?.date_format,
              dateTimeFormat:
                result.userState?.dateTimeFormat ??
                result.uiSetting?.date_time_format,
              timeFormat:
                result.userState?.timeFormat ?? result.uiSetting?.time_format,
              timeZone:
                result.userState?.timeZone ?? result.uiSetting?.time_zone,
              firstLogin: result.userState?.firstLogin,
              fontSize:
                result.userState?.fontSize ?? DEFAULT_USER_PREFERENCES.fontSize,
              layoutMode: result.userState?.layoutMode,
              menuMode:
                result.userState?.menuMode ?? DEFAULT_USER_PREFERENCES.menuMode,
              user: result.userState?.user,
              language:
                result.userState?.language ??
                result.uiSetting?.primary_language,
              theme: result.userState?.theme ?? result.uiSetting?.default_theme,
              tourMode: tourConfig,
            };
        dispatch(
          new UserPreferencesActions.SetUserPreferences(userStateSetting),
        );
        setState(
          patch<UserPreferencesStateModel>({
            userState: userStateSetting,
            userUiSettings: result.uiSetting,
          }),
        );
      }),
    );
  }
  @Action(UserPreferencesActions.UpdateUserStateData)
  UpdateUserState(
    { setState, dispatch }: StateContext<UserPreferencesStateModel>,
    { payload }: UserPreferencesActions.UpdateUserStateData,
  ): Observable<any> {
    return this.userControllerService
      .updateUserState({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
        body: payload,
      })
      .pipe(
        map((updateUserState: UserStateDataEntryValue) =>
          this.UserStateExcludingFactoryService.createPresenterModel(
            updateUserState,
          ),
        ),
        tap((updateUserState) => {
          sessionStorage.removeItem('configuration');
          dispatch(new SetLanguage(updateUserState.language.code));
          setState(
            patch<UserPreferencesStateModel>({
              userState: updateUserState,
            }),
          );
        }),
      );
  }

  @Action(UserPreferencesActions.ResetUserStateToDefault)
  resetUserStateToDefault(
    { getState, setState, dispatch }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.ResetUserStateToDefault,
  ): Observable<any> {
    const userSetting = getState().userUiSettings;

    return this.userControllerService
      .resetUserState({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap(() => {
          sessionStorage.removeItem('configuration');
          dispatch(
            new SetLanguage(userSetting.primary_language?._resolved?.mainTitle),
          );
          setState(
            patch<UserPreferencesStateModel>({
              userState: {},
            }),
          );
        }),
      );
  }

  @Action(UserPreferencesActions.GetBackendVersion)
  getBackendVersion(
    { setState, getState }: StateContext<UserPreferencesStateModel>,
    {}: UserPreferencesActions.GetBackendVersion,
  ): Observable<any> {
    // const existingBackendVersion = getState().backendVersion;
    // if (!!existingBackendVersion) {
    //   return of(existingBackendVersion);
    // }
    return this.versionControllerService
      .getVersion({
        context: new ManagedHttpContext({
          bypassPublicMiddleware: true,
        }),
      })
      .pipe(
        tap((res) => {
          setState(
            patch<UserPreferencesStateModel>({
              backendVersion: res?.backendVersion,
            }),
          );
        }),
      );
  }

  @Action(UserPreferencesActions.SetActiveDelegation)
  setDelegation(
    { setState }: StateContext<UserPreferencesStateModel>,
    { delegationId }: UserPreferencesActions.SetActiveDelegation,
  ) {
    setState(
      patch<UserPreferencesStateModel>({
        delegation: {
          activeDelegationId: delegationId,
        },
      }),
    );

    this.document.defaultView.location.href =
      this.document.defaultView.location.origin;
  }
}
